import React, { useState, useEffect } from "react";
import { Phone, Mail, MapPin, MessageCircle, Send, Loader2 } from "lucide-react";
import { BUSINESS } from "@/data/site";
import { submitBooking } from "@/lib/bookingApi";
import SuccessModal from "@/components/SuccessModal";
import Seo from "@/components/Seo";

const wa = (n, m) =>
  `https://wa.me/91${n}?text=${encodeURIComponent(m || "Hello Chakri Travels, I want to book a car rental service.")}`;

const CAR_TYPES = [
  "Executive Sedan",
  "Premium SUV",
  "Wedding Special",
  "Force Urbania",
  "Tempo Traveller",
  "Tourist Coach",
];

const initialForm = {
  customerName: "",
  customerPhone: "",
  customerEmail: "",
  pickupLocation: "",
  dropLocation: "",
  pickupDate: "",
  pickupTime: "",
  carType: CAR_TYPES[0],
  passengers: "2",
  specialRequest: "",
};

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const [popup, setPopup] = useState({ open: false, mode: "api", record: null });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      customerName: form.customerName,
      customerPhone: form.customerPhone,
      pickupLocation: form.pickupLocation,
      dropLocation: form.dropLocation,
      pickupDate: form.pickupDate,
      pickupTime: form.pickupTime || "09:00",
      carType: form.carType,
      passengers: parseInt(form.passengers, 10) || 2,
      specialRequest: form.specialRequest,
    };
    const result = await submitBooking(payload);
    setBusy(false);
    setPopup({ open: true, mode: result.mode, record: result.record });
    if (result.mode === "api") setForm(initialForm);
  };

  useEffect(() => {
    document.title = `Contact Chakri Travels — Book a Premium Car | ${BUSINESS.domain}`;
  }, []);

  const mapEmbed = `https://www.google.com/maps?q=${encodeURIComponent(BUSINESS.mapQuery)}&output=embed`;
  const mapLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(BUSINESS.mapQuery)}`;

  return (
    <main>
      <Seo
        title="Contact Chakri Travels — Book Car Rental in Kakinada"
        description="Book a premium car with Chakri Travels. Sarpavaram, Kakinada — call +91 90591 14439 or +91 90323 91199, or WhatsApp instantly."
        path="/contact"
      />
      {/* Hero */}
      <section className="relative isolate pt-28 pb-12 lg:pt-36 gradient-royal text-white overflow-hidden">
        <div className="absolute -left-32 top-0 h-96 w-96 rounded-full bg-[var(--gold)]/20 blur-3xl" />
        <div className="absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/60 glass-dark px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--gold)]">
              Get in touch
            </div>
            <h1 className="font-display text-3xl font-bold leading-[1.1] text-white sm:text-5xl lg:text-6xl">
              Let's plan your <span className="text-[var(--gold)]">next journey</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base font-medium text-white/90 sm:text-lg">
              Submit the form below and our concierge team will WhatsApp you back within minutes.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <div className="grid gap-10 lg:grid-cols-[1.4fr_1fr]">
          {/* Form */}
          <div className="rounded-3xl bg-white p-6 sm:p-10 ring-1 ring-[var(--royal)]/10 shadow-[0_25px_70px_-25px_rgba(15,23,80,0.35)]">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/40 bg-[var(--gold)]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--royal-deep)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--gold)]"></span> Book a car
            </div>
            <h2 className="font-display text-3xl font-bold text-[var(--royal-deep)] sm:text-4xl">
              Tell us about your <span className="text-gradient-gold">trip</span>
            </h2>
            <p className="mt-3 text-sm text-muted-foreground">
              You'll receive a WhatsApp confirmation the moment our team accepts.
            </p>

            <form onSubmit={submit} className="mt-8 grid gap-4 sm:grid-cols-2" data-testid="contact-form" noValidate>
              <Field label="Full Name *">
                <input data-testid="ct-name" required value={form.customerName} onChange={set("customerName")} className="form-input" />
              </Field>
              <Field label="Mobile Number *">
                <input data-testid="ct-mobile" required type="tel" pattern="[0-9 +]{10,15}" value={form.customerPhone} onChange={set("customerPhone")} className="form-input" placeholder="9876543210" />
              </Field>
              <Field label="Email Address">
                <input data-testid="ct-email" type="email" value={form.customerEmail} onChange={set("customerEmail")} className="form-input" />
              </Field>
              <Field label="Pickup Location *">
                <input data-testid="ct-pickup" required value={form.pickupLocation} onChange={set("pickupLocation")} className="form-input" placeholder="Kakinada Airport" />
              </Field>
              <Field label="Drop Location *">
                <input data-testid="ct-drop" required value={form.dropLocation} onChange={set("dropLocation")} className="form-input" placeholder="Rajamahendravaram" />
              </Field>
              <Field label="Travel Date *">
                <input data-testid="ct-date" required type="date" value={form.pickupDate} onChange={set("pickupDate")} className="form-input" />
              </Field>
              <Field label="Pickup Time">
                <input data-testid="ct-time" type="time" value={form.pickupTime} onChange={set("pickupTime")} className="form-input" />
              </Field>
              <Field label="Vehicle *">
                <select data-testid="ct-car" value={form.carType} onChange={set("carType")} className="form-input">
                  {CAR_TYPES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </Field>
              <Field label="Passengers *">
                <input data-testid="ct-pax" required type="number" min="1" max="60" value={form.passengers} onChange={set("passengers")} className="form-input" />
              </Field>
              <Field label="Special Request" full>
                <textarea data-testid="ct-message" rows="3" value={form.specialRequest} onChange={set("specialRequest")} className="form-input" placeholder="Child seat, decorated for wedding, English-speaking chauffeur, etc."></textarea>
              </Field>

              <button
                data-testid="ct-submit"
                type="submit"
                disabled={busy}
                className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-xl gradient-royal px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[var(--royal)]/30 transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {busy ? (<><Loader2 size={16} className="animate-spin" /> Sending…</>) : (<><Send size={16} /> Send Booking Enquiry</>)}
              </button>
            </form>
          </div>

          {/* Right column */}
          <div className="space-y-5">
            <InfoCard icon={Phone} label="Call us">
              <div className="space-y-1">
                {BUSINESS.phones.map((p, i) => (
                  <a key={p} data-testid={`phone-link-${i}`} href={`tel:+91${p}`} className="block font-display text-lg font-semibold text-[var(--royal-deep)] hover:text-[var(--gold)]">
                    +91 {p}
                  </a>
                ))}
              </div>
            </InfoCard>

            <InfoCard icon={Mail} label="Email">
              <a href={`mailto:${BUSINESS.email}`} className="block font-display text-base text-[var(--royal-deep)] hover:text-[var(--gold)] break-all">
                {BUSINESS.email}
              </a>
            </InfoCard>

            <InfoCard icon={MapPin} label="Visit">
              <p data-testid="business-address" className="text-base text-[var(--royal-deep)] leading-relaxed">{BUSINESS.address}</p>
              <a data-testid="open-maps-link" href={mapLink} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[var(--royal)] hover:text-[var(--gold)]">
                Open in Google Maps →
              </a>
            </InfoCard>

            <div className="rounded-2xl gradient-royal p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl gradient-gold text-[var(--royal-deep)]">
                  <MessageCircle size={18} />
                </div>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--gold)]">Instant chat</p>
                  <p className="mt-1 text-sm text-white/85">WhatsApp us now</p>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <a data-testid="wa-primary-btn" href={wa(BUSINESS.primaryWhatsApp)} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-4 py-2.5 text-xs font-semibold text-white">
                      <MessageCircle size={14} /> {BUSINESS.primaryWhatsApp}
                    </a>
                    <a data-testid="wa-secondary-btn" href={wa(BUSINESS.secondaryWhatsApp)} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-4 py-2.5 text-xs font-semibold text-white">
                      <MessageCircle size={14} /> {BUSINESS.secondaryWhatsApp}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Google Map */}
        <div className="mt-12">
          <iframe data-testid="google-map-iframe" title="Chakri Travels location" src={mapEmbed} className="map-frame" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
        </div>
      </section>

      <SuccessModal open={popup.open} mode={popup.mode} booking={popup.record} onClose={() => setPopup({ ...popup, open: false })} />
    </main>
  );
};

const Field = ({ label, children, full }) => (
  <label className={`block ${full ? "sm:col-span-2" : ""}`}>
    <span className="block text-xs font-semibold text-[var(--royal-deep)] uppercase tracking-wider mb-1.5">{label}</span>
    {children}
  </label>
);

const InfoCard = ({ icon: Icon, label, children }) => (
  <div className="rounded-2xl bg-white p-6 ring-1 ring-[var(--royal)]/10 shadow-[0_10px_30px_-15px_rgba(15,23,80,0.18)]">
    <div className="flex items-start gap-4">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl gradient-royal text-white">
        <Icon size={18} />
      </div>
      <div className="flex-1">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--royal)]">{label}</p>
        <div className="mt-2">{children}</div>
      </div>
    </div>
  </div>
);

export default Contact;
